import React, { FC, useRef } from 'react';
import { Tabs } from 'antd';
import cx from 'classnames';
import { getLangKey } from '../../models/Language';
import * as translations from './strings';
import styles from './styles.module.css';

const strings = translations[getLangKey()];

export const TASK_DETAIL_TABS = [
  'details',
  'comments',
  'session',
  'integrations'
] as const;

export type TaskDetailTabKey = typeof TASK_DETAIL_TABS[number];

const TAB_LABELS: Record<TaskDetailTabKey, string> = {
  details: strings.details,
  comments: strings.comments,
  session: strings.session,
  integrations: strings.integrations
};

type Props = {
  activeKey: TaskDetailTabKey;
  onChange: (tab: TaskDetailTabKey) => void;
  commentCount: number;
  showSession?: boolean;
  showIntegrations?: boolean;
};

const TaskDetailTabs: FC<Props> = ({
  activeKey,
  onChange,
  commentCount,
  showSession = false,
  showIntegrations = false
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const tabs = TASK_DETAIL_TABS.filter(tab => {
    if (tab === 'session') return showSession;
    if (tab === 'integrations') return showIntegrations;
    return true;
  });
  const selected = tabs.includes(activeKey) ? activeKey : 'details';

  const labelFor = (tab: TaskDetailTabKey) => {
    if (tab === 'comments') {
      return (
        <span className={styles.label}>
          {TAB_LABELS.comments}
          <span
            className={cx(styles.count, {
              [styles.countActive]: selected === 'comments'
            })}
          >
            {commentCount}
          </span>
        </span>
      );
    }

    if (tab === 'integrations') {
      return (
        <span className={styles.integrationsLabel}>
          {TAB_LABELS.integrations}
        </span>
      );
    }

    return TAB_LABELS[tab];
  };

  const selectTab = (tab: TaskDetailTabKey) => {
    if (tab !== selected) onChange(tab);
    const button = barRef.current?.querySelector<HTMLElement>(
      `[data-node-key="${tab}"] [role="tab"]`
    );
    if (button) button.focus();
  };

  return (
    <div
      className={styles.bar}
      ref={barRef}
      onKeyDown={event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        const target = event.target as HTMLElement;
        if (target.getAttribute('role') !== 'tab') return;

        event.preventDefault();
        const index = tabs.indexOf(selected);
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(index + delta + tabs.length) % tabs.length];
        selectTab(next);
      }}
    >
      <Tabs
        className={styles.tabs}
        activeKey={selected}
        tabBarGutter={0}
        animated={false}
        onChange={key => onChange(key as TaskDetailTabKey)}
        items={tabs.map(tab => ({
          key: tab,
          label: labelFor(tab)
        }))}
      />
    </div>
  );
};

export default TaskDetailTabs;
